/**
 * School Growth Analytics page (B-2026-0076).
 *
 * Route: /channels/:slug/analytics
 * Access: channel owner / channel_admin only (enforced by backend).
 *
 * Panels:
 *   1. Growth     — weekly member-count area chart + hero numbers
 *   2. Engagement — weekly eval-volume bar chart + top templates
 *   3. Token Runway — conditional: null credit-ledger → placeholder;
 *                     non-null → burn rate + runway + CTA when < 14 days
 *   4. At-Risk Athletes — churn_candidates list
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import Spinner from '@/components/ui/Spinner';
import { getErrorMessage } from '@/utils/errors';
import {
  getChannelAnalyticsOverview,
  getChannelMemberGrowth,
  getChannelAthleteActivity,
} from '@/services/channelAnalyticsApi';
import type {
  ChannelAnalyticsOverview,
  ChannelMemberGrowth,
  ChannelAthleteActivity,
  AthleteActivityItem,
} from '@/types/channelAnalytics';

// ---------------------------------------------------------------------------
// Shared panel helpers
// ---------------------------------------------------------------------------

interface PanelState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

function initPanel<T>(): PanelState<T> {
  return { data: null, loading: true, error: null };
}

function PanelShell({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        backgroundColor: 'var(--color-dark-elevated)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: '16px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      <h2
        style={{
          margin: 0,
          fontSize: '15px',
          fontWeight: 600,
          color: 'var(--color-text-primary)',
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function PanelLoading() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
      <Spinner />
    </div>
  );
}

function PanelError({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        padding: '16px',
        borderRadius: '10px',
        backgroundColor: 'rgba(240,93,94,0.08)',
        border: '1px solid rgba(240,93,94,0.25)',
        color: 'var(--color-danger)',
        fontSize: '13px',
        textAlign: 'center',
      }}
    >
      {message}
    </div>
  );
}

function HeroStat({ label, value }: { label: string; value: string | number }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        flex: 1,
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontSize: '24px',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          lineHeight: 1,
        }}
      >
        {value}
      </span>
      <span
        style={{
          fontSize: '12px',
          color: 'var(--color-text-muted)',
          fontWeight: 400,
        }}
      >
        {label}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trend badge
// ---------------------------------------------------------------------------

const TREND_COLORS: Record<string, string> = {
  improving: 'rgba(52,211,153,0.18)',
  plateauing: 'rgba(244,201,93,0.18)',
  declining: 'rgba(240,93,94,0.15)',
  inactive: 'rgba(148,163,184,0.12)',
};
const TREND_TEXT_COLORS: Record<string, string> = {
  improving: '#34d399',
  plateauing: '#f4c95d',
  declining: '#f05d5e',
  inactive: '#94a3b8',
};

function TrendBadge({ trend }: { trend: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: 600,
        backgroundColor: TREND_COLORS[trend] ?? TREND_COLORS.inactive,
        color: TREND_TEXT_COLORS[trend] ?? TREND_TEXT_COLORS.inactive,
        letterSpacing: '0.02em',
        textTransform: 'capitalize',
      }}
    >
      {trend}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Growth panel
// ---------------------------------------------------------------------------

function GrowthPanel({
  overview,
  growth,
}: {
  overview: ChannelAnalyticsOverview;
  growth: ChannelMemberGrowth | null;
}) {
  return (
    <>
      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
        <HeroStat label="Total members" value={overview.total_members} />
        <HeroStat label="Active (30d)" value={overview.active_members_30d} />
        <HeroStat label="New (30d)" value={overview.new_members_30d} />
      </div>

      {growth && growth.buckets.length > 0 ? (
        <div style={{ height: 160 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={growth.buckets} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="memberGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3da9fc" stopOpacity={0.28} />
                  <stop offset="95%" stopColor="#3da9fc" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
              <XAxis
                dataKey="week_start"
                tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
                tickFormatter={(v: string) => v.slice(5)}
              />
              <YAxis tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-dark-elevated-2)',
                  border: '1px solid var(--color-border-subtle)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: 'var(--color-text-primary)',
                }}
                labelStyle={{ color: 'var(--color-text-muted)' }}
              />
              <Area
                type="monotone"
                dataKey="cumulative_total"
                name="Members"
                stroke="#3da9fc"
                fill="url(#memberGrad)"
                strokeWidth={2}
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', margin: 0 }}>
          No membership data yet.
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Engagement panel
// ---------------------------------------------------------------------------

function EngagementPanel({ overview }: { overview: ChannelAnalyticsOverview }) {
  return (
    <>
      <HeroStat label="Evaluations (30d)" value={overview.total_evals_30d} />

      {/* Weekly eval volume bar chart */}
      {overview.eval_volume_by_week.length > 0 && (
        <div style={{ height: 140 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={overview.eval_volume_by_week}
              margin={{ top: 4, right: 8, left: -20, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" vertical={false} />
              <XAxis
                dataKey="week_start"
                tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
                tickFormatter={(v: string) => v.slice(5)}
              />
              <YAxis tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-dark-elevated-2)',
                  border: '1px solid var(--color-border-subtle)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: 'var(--color-text-primary)',
                }}
                labelStyle={{ color: 'var(--color-text-muted)' }}
              />
              <Bar dataKey="eval_count" name="Evals" fill="#7c6bf6" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top templates grid */}
      {overview.top_templates_30d.length > 0 && (
        <div>
          <h3
            style={{
              margin: '0 0 10px',
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--color-text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Top Templates (30d)
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {overview.top_templates_30d.map((tmpl, i) => (
              <div
                key={tmpl.template_id ?? i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  backgroundColor: 'var(--color-dark-elevated-2)',
                }}
              >
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: 'var(--color-text-muted)',
                    minWidth: '16px',
                  }}
                >
                  #{i + 1}
                </span>
                <span
                  style={{
                    flex: 1,
                    fontSize: '13px',
                    fontWeight: 500,
                    color: 'var(--color-text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tmpl.template_name ?? '(deleted template)'}
                </span>
                <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                  {tmpl.eval_count} eval{tmpl.eval_count !== 1 ? 's' : ''}
                </span>
                {tmpl.avg_score != null && (
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: 'var(--color-text-secondary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    avg {Math.round(tmpl.avg_score)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Token Runway panel
// ---------------------------------------------------------------------------

const LOW_RUNWAY_DAYS = 14;

function TokenRunwayPanel({ overview }: { overview: ChannelAnalyticsOverview }) {
  const { token_burn_rate_30d, estimated_days_remaining } = overview;

  // Credit ledger is off — show placeholder rather than dead-ending the feature.
  if (token_burn_rate_30d === null) {
    return (
      <p
        style={{
          margin: 0,
          fontSize: '14px',
          color: 'var(--color-text-muted)',
          textAlign: 'center',
          padding: '24px 0',
        }}
      >
        Token analytics coming soon. Activate the credit economy to see burn rate and runway here.
      </p>
    );
  }

  const isLowRunway =
    estimated_days_remaining !== null && estimated_days_remaining < LOW_RUNWAY_DAYS;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
        <HeroStat label="Credits burned (30d)" value={token_burn_rate_30d.toLocaleString()} />
        <HeroStat
          label="Estimated days remaining"
          value={estimated_days_remaining != null ? `${estimated_days_remaining}d` : '—'}
        />
      </div>

      {/* Urgent CTA when runway < 14 days */}
      {isLowRunway && (
        <div
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            padding: '12px 16px',
            borderRadius: '12px',
            backgroundColor: 'rgba(240,93,94,0.08)',
            border: '1px solid rgba(240,93,94,0.28)',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: '13px', color: 'var(--color-danger)', fontWeight: 500 }}>
            Low runway — only {estimated_days_remaining} day{estimated_days_remaining !== 1 ? 's' : ''} of credits
            remaining.
          </span>
          <a
            href="/credits"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '6px 14px',
              borderRadius: '999px',
              backgroundColor: 'var(--color-danger)',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 600,
              textDecoration: 'none',
              flexShrink: 0,
            }}
          >
            Buy more tokens
          </a>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Athlete activity table
// ---------------------------------------------------------------------------

function AthleteActivityTable({ activity }: { activity: ChannelAthleteActivity }) {
  if (activity.athletes.length === 0) {
    return (
      <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', margin: 0, textAlign: 'center', padding: '16px 0' }}>
        No members yet.
      </p>
    );
  }

  return (
    <div
      style={{
        overflowX: 'auto',
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr>
            {['Athlete', 'Evals', 'Last Eval', 'Avg Score', 'Trend'].map((h) => (
              <th
                key={h}
                style={{
                  textAlign: 'left',
                  padding: '6px 10px',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--color-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  borderBottom: '1px solid var(--color-border-subtle)',
                  whiteSpace: 'nowrap',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {activity.athletes.map((a: AthleteActivityItem) => (
            <tr
              key={a.user_id}
              style={{ borderBottom: '1px solid var(--color-border-subtle)' }}
            >
              <td
                style={{
                  padding: '8px 10px',
                  color: 'var(--color-text-primary)',
                  fontWeight: 500,
                  maxWidth: '180px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {a.display_name ?? '(unknown)'}
              </td>
              <td style={{ padding: '8px 10px', color: 'var(--color-text-secondary)' }}>
                {a.eval_count_in_period}
              </td>
              <td style={{ padding: '8px 10px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                {a.last_eval_at
                  ? new Date(a.last_eval_at).toLocaleDateString()
                  : '—'}
              </td>
              <td style={{ padding: '8px 10px', color: 'var(--color-text-secondary)' }}>
                {a.avg_score_in_period != null ? Math.round(a.avg_score_in_period) : '—'}
              </td>
              <td style={{ padding: '8px 10px' }}>
                <TrendBadge trend={a.trend} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// At-Risk Athletes card (churn_candidates)
// ---------------------------------------------------------------------------

function AtRiskCard({ overview }: { overview: ChannelAnalyticsOverview }) {
  const { churn_candidates } = overview;

  if (churn_candidates.length === 0) {
    return (
      <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', margin: 0, textAlign: 'center', padding: '8px 0' }}>
        No at-risk athletes — everyone is active. Great work!
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <p style={{ margin: '0 0 4px', fontSize: '13px', color: 'var(--color-text-muted)' }}>
        {churn_candidates.length} athlete{churn_candidates.length !== 1 ? 's have' : ' has'} not submitted
        an evaluation in over 60 days.
      </p>
      {churn_candidates.map((c) => (
        <div
          key={c.user_id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 14px',
            borderRadius: '10px',
            backgroundColor: 'var(--color-dark-elevated-2)',
          }}
        >
          <span
            style={{
              flex: 1,
              fontSize: '13px',
              fontWeight: 500,
              color: 'var(--color-text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {c.display_name ?? '(unknown)'}
          </span>
          <span
            style={{ fontSize: '12px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}
          >
            Last eval:{' '}
            {new Date(c.last_eval_at).toLocaleDateString()}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export default function ChannelAnalyticsPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [overview, setOverview] = useState<PanelState<ChannelAnalyticsOverview>>(initPanel());
  const [growth, setGrowth] = useState<PanelState<ChannelMemberGrowth>>(initPanel());
  const [activity, setActivity] = useState<PanelState<ChannelAthleteActivity>>(initPanel());

  const load = useCallback(async () => {
    if (!slug) return;

    // Fire all three requests in parallel.
    const [ovResult, grResult, acResult] = await Promise.allSettled([
      getChannelAnalyticsOverview(slug),
      getChannelMemberGrowth(slug, 12),
      getChannelAthleteActivity(slug, 30),
    ]);

    setOverview({
      data: ovResult.status === 'fulfilled' ? ovResult.value : null,
      loading: false,
      error:
        ovResult.status === 'rejected'
          ? getErrorMessage(ovResult.reason)
          : null,
    });
    setGrowth({
      data: grResult.status === 'fulfilled' ? grResult.value : null,
      loading: false,
      error:
        grResult.status === 'rejected'
          ? getErrorMessage(grResult.reason)
          : null,
    });
    setActivity({
      data: acResult.status === 'fulfilled' ? acResult.value : null,
      loading: false,
      error:
        acResult.status === 'rejected'
          ? getErrorMessage(acResult.reason)
          : null,
    });
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  const anyLoading = overview.loading || growth.loading || activity.loading;

  return (
    <div
      style={{
        maxWidth: 900,
        margin: '0 auto',
        padding: '24px 20px 48px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        <button
          type="button"
          onClick={() => navigate(`/channels/${slug}/manage`)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            borderRadius: '999px',
            backgroundColor: 'var(--color-dark-elevated-2)',
            border: '1px solid var(--color-border-subtle)',
            color: 'var(--color-text-secondary)',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <ArrowLeftIcon style={{ width: 14, height: 14 }} />
          Back
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: 'var(--color-text-primary)' }}>
            School Analytics
          </h1>
          {slug && (
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--color-text-muted)' }}>
              {slug}
            </p>
          )}
        </div>
        {anyLoading && (
          <div style={{ marginLeft: 'auto' }}>
            <Spinner />
          </div>
        )}
      </div>

      {/* ── Growth panel ────────────────────────────────────────────────── */}
      <PanelShell title="Member Growth">
        {overview.loading || growth.loading ? (
          <PanelLoading />
        ) : overview.error ? (
          <PanelError message={overview.error} />
        ) : overview.data ? (
          <GrowthPanel overview={overview.data} growth={growth.data} />
        ) : null}
      </PanelShell>

      {/* ── Engagement panel ─────────────────────────────────────────────── */}
      <PanelShell title="Evaluation Engagement">
        {overview.loading ? (
          <PanelLoading />
        ) : overview.error ? (
          <PanelError message={overview.error} />
        ) : overview.data ? (
          <EngagementPanel overview={overview.data} />
        ) : null}
      </PanelShell>

      {/* ── Token Runway panel ───────────────────────────────────────────── */}
      <PanelShell title="Token Runway">
        {overview.loading ? (
          <PanelLoading />
        ) : overview.error ? (
          <PanelError message={overview.error} />
        ) : overview.data ? (
          <TokenRunwayPanel overview={overview.data} />
        ) : null}
      </PanelShell>

      {/* ── At-Risk Athletes panel ───────────────────────────────────────── */}
      <PanelShell title="At-Risk Athletes">
        {overview.loading ? (
          <PanelLoading />
        ) : overview.error ? (
          <PanelError message={overview.error} />
        ) : overview.data ? (
          <AtRiskCard overview={overview.data} />
        ) : null}
      </PanelShell>

      {/* ── Athlete Activity panel ───────────────────────────────────────── */}
      <PanelShell title={`Athlete Activity (${activity.data?.days ?? 30}d)`}>
        {activity.loading ? (
          <PanelLoading />
        ) : activity.error ? (
          <PanelError message={activity.error} />
        ) : activity.data ? (
          <AthleteActivityTable activity={activity.data} />
        ) : null}
      </PanelShell>
    </div>
  );
}
